import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { isInitialized, event, requestUAScript, trackEvent } from '@zillow/universal-analytics';
import PropTypes from 'prop-types';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _toArray(arr) {
  return _arrayWithHoles(arr) || _iterableToArray(arr) || _nonIterableRest();
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _nonIterableSpread();
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) {
    for (var i = 0, arr2 = new Array(arr.length); i < arr.length; i++) arr2[i] = arr[i];

    return arr2;
  }
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArray(iter) {
  if (Symbol.iterator in Object(iter) || Object.prototype.toString.call(iter) === "[object Arguments]") return Array.from(iter);
}

function _iterableToArrayLimit(arr, i) {
  if (!(Symbol.iterator in Object(arr) || Object.prototype.toString.call(arr) === "[object Arguments]")) {
    return;
  }

  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance");
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

var canUseSymbol = typeof Symbol === 'function' && typeof Symbol.for === 'function';
function getCsatAnalyticsContext() {
  var contextKey = canUseSymbol ? Symbol.for('__CSAT_ANALYTICS_CONTEXT__') : '__CSAT_ANALYTICS_CONTEXT__';
  var context = React.createContext[contextKey];

  if (!context) {
    Object.defineProperty(React.createContext, contextKey, {
      configurable: true,
      enumerable: false,
      value: context = React.createContext({
        trackClickstreamEvent: function trackClickstreamEvent() {}
      }),
      writable: false
    });
    context.displayName = 'CsatAnalyticsContext';
  }

  return context;
}

var AnalyticsContext = function AnalyticsContext(props) {
  var isInit = isInitialized();

  var _useState = useState([]),
      _useState2 = _slicedToArray(_useState, 2),
      eventQueue = _useState2[0],
      setEventQueue = _useState2[1];

  var fromApp = props.fromApp;
  useEffect(function () {
    if (eventQueue.length > 0 && isInit) {
      var _eventQueue = _toArray(eventQueue),
          _eventQueue$ = _eventQueue[0],
          properties = _eventQueue$.properties,
          options = _eventQueue$.options,
          rest = _eventQueue.slice(1);

      if (!fromApp) {
        event(properties, options);
      }

      setEventQueue(rest);
    }
  }, [eventQueue.length, isInit]);
  var trackClickstreamEvent = useCallback(function (properties, options) {
    setEventQueue(function (prev) {
      return [].concat(_toConsumableArray(prev), [{
        properties: properties,
        options: options
      }]);
    });

    if (!isInit) {
      console.warn('IntegrationAnalyticsContext: Analytics event queued before ready');
    }
  }, [isInit]);
  var Context = getCsatAnalyticsContext();
  var contextValue = useMemo(function () {
    return {
      trackClickstreamEvent: trackClickstreamEvent
    };
  }, [trackClickstreamEvent]);
  return React.createElement(Context.Provider, _extends({
    value: contextValue
  }, props));
};

AnalyticsContext.propTypes = process.env.NODE_ENV !== "production" ? {
  fromApp: PropTypes.bool
} : {};

/**
 * @returns {Function} - to push a tracking event to Clickstream.
 */

var useTrackClickstream = function useTrackClickstream() {
  var _useContext = useContext(getCsatAnalyticsContext()),
      trackClickstreamEvent = _useContext.trackClickstreamEvent;

  return trackClickstreamEvent;
};

var csatAnalyticsTracking = function csatAnalyticsTracking(isTrack, events) {
  if (typeof document !== 'undefined' && !isInitialized()) {
    requestUAScript();
  }

  if (isTrack) {
    events.forEach(function (param) {
      trackEvent(param);
    });
  }
};

var TriggerType = {
  IMPRESSION: 'impression',
  INTERACTION: 'interaction'
};
var TriggerObject = {
  NEXT_BUTTON: 'next_button',
  CLOSE_BUTTON: 'close_button',
  EMAIL_FIELD: 'email_field',
  PHONE_FIELD: 'phone_field',
  ZG_LOGO: 'zg_logo',
  CLOSE_PAGE: 'close_page'
};
var questionClickstreamFieldsAvailable = function questionClickstreamFieldsAvailable(survey, questionStep) {
  var _question$clickstream;

  var _ref = survey || {},
      _ref$questions = _ref.questions,
      questions = _ref$questions === void 0 ? {} : _ref$questions;

  var question = questions === null || questions === void 0 ? void 0 : questions[questionStep];
  var triggerSource = (question === null || question === void 0 ? void 0 : (_question$clickstream = question.clickstreamMetadata) === null || _question$clickstream === void 0 ? void 0 : _question$clickstream['trigger_source_nm']) || '';
  return triggerSource !== '';
};
var errorStatesClickstreamFieldsAvailable = function errorStatesClickstreamFieldsAvailable(survey) {
  var _alreadyCompletedMeta;

  var _ref2 = survey || {},
      _ref2$errorStates = _ref2.errorStates,
      errorStates = _ref2$errorStates === void 0 ? {} : _ref2$errorStates;

  var alreadyCompletedMetadata = errorStates === null || errorStates === void 0 ? void 0 : errorStates.alreadyCompleted;
  var triggerSource = (alreadyCompletedMetadata === null || alreadyCompletedMetadata === void 0 ? void 0 : (_alreadyCompletedMeta = alreadyCompletedMetadata.clickstreamMetadata) === null || _alreadyCompletedMeta === void 0 ? void 0 : _alreadyCompletedMeta['trigger_source_nm']) || '';
  return triggerSource !== '';
};
var csatFormViewEventProperties = {
  envelope: {
    event_client_start_dtm: new Date().toISOString(),
    event_template_id: '341',
    event_template_version_id: '1',
    event_type_id: '5583',
    event_type_version_id: '1'
  },
  semantic: {
    semantic_event_nm: 'view_content',
    topic_tag_txt: []
  }
};

var generateContextualDataFields = function generateContextualDataFields(blockName, surveyInstanceID, surveyName) {
  var _ref3;

  if (surveyInstanceID === void 0) {
    surveyInstanceID = '';
  }

  if (surveyName === void 0) {
    surveyName = '';
  }

  return _ref3 = {}, _ref3[blockName] = {
    survey_instance_id: surveyInstanceID,
    survey_nm: surveyName
  }, _ref3;
};

var generateClickstreamFields = function generateClickstreamFields(survey, questionStep, triggerObject) {
  var _question$clickstream2, _question$clickstream3, _clickstreamMetadata$;

  var _ref4 = survey || {},
      _ref4$questions = _ref4.questions,
      questions = _ref4$questions === void 0 ? {} : _ref4$questions,
      _ref4$clickstreamMeta = _ref4.clickstreamMetadata,
      clickstreamMetadata = _ref4$clickstreamMeta === void 0 ? {} : _ref4$clickstreamMeta,
      _ref4$surveyInstanceI = _ref4.surveyInstanceID,
      surveyInstanceID = _ref4$surveyInstanceI === void 0 ? '' : _ref4$surveyInstanceI,
      _ref4$surveyName = _ref4.surveyName,
      surveyName = _ref4$surveyName === void 0 ? '' : _ref4$surveyName;

  var question = questions === null || questions === void 0 ? void 0 : questions[questionStep];
  var clickstreamFields = {
    trigger_source_nm: (question === null || question === void 0 ? void 0 : (_question$clickstream2 = question.clickstreamMetadata) === null || _question$clickstream2 === void 0 ? void 0 : _question$clickstream2['trigger_source_nm']) || '',
    trigger_location_nm: (question === null || question === void 0 ? void 0 : (_question$clickstream3 = question.clickstreamMetadata) === null || _question$clickstream3 === void 0 ? void 0 : _question$clickstream3['trigger_location_nm']) || '',
    trigger_object_nm: triggerObject ? (clickstreamMetadata === null || clickstreamMetadata === void 0 ? void 0 : clickstreamMetadata['trigger_object_nm_prefix']) + "|" + triggerObject : 'no_trigger_object'
  };
  var contextualBlockName = clickstreamMetadata === null || clickstreamMetadata === void 0 ? void 0 : (_clickstreamMetadata$ = clickstreamMetadata['contextual_block']) === null || _clickstreamMetadata$ === void 0 ? void 0 : _clickstreamMetadata$[0];
  var contextualBody = contextualBlockName ? generateContextualDataFields(contextualBlockName, surveyInstanceID, surveyName) : {};
  return {
    clickstreamFields: clickstreamFields,
    contextualBody: contextualBody
  };
};
var generateClickstreamCompletedStatesFields = function generateClickstreamCompletedStatesFields(survey, triggerObject) {
  var _alreadyCompletedMeta2, _alreadyCompletedMeta3, _clickstreamMetadata$2;

  var _ref5 = survey || {},
      _ref5$errorStates = _ref5.errorStates,
      errorStates = _ref5$errorStates === void 0 ? {} : _ref5$errorStates,
      _ref5$clickstreamMeta = _ref5.clickstreamMetadata,
      clickstreamMetadata = _ref5$clickstreamMeta === void 0 ? {} : _ref5$clickstreamMeta,
      _ref5$surveyInstanceI = _ref5.surveyInstanceID,
      surveyInstanceID = _ref5$surveyInstanceI === void 0 ? '' : _ref5$surveyInstanceI,
      _ref5$surveyName = _ref5.surveyName,
      surveyName = _ref5$surveyName === void 0 ? '' : _ref5$surveyName;

  var alreadyCompletedMetadata = errorStates === null || errorStates === void 0 ? void 0 : errorStates.alreadyCompleted;
  var clickstreamFields = {
    trigger_source_nm: (alreadyCompletedMetadata === null || alreadyCompletedMetadata === void 0 ? void 0 : (_alreadyCompletedMeta2 = alreadyCompletedMetadata.clickstreamMetadata) === null || _alreadyCompletedMeta2 === void 0 ? void 0 : _alreadyCompletedMeta2['trigger_source_nm']) || '',
    trigger_location_nm: (alreadyCompletedMetadata === null || alreadyCompletedMetadata === void 0 ? void 0 : (_alreadyCompletedMeta3 = alreadyCompletedMetadata.clickstreamMetadata) === null || _alreadyCompletedMeta3 === void 0 ? void 0 : _alreadyCompletedMeta3['trigger_location_nm']) || '',
    trigger_object_nm: triggerObject ? (clickstreamMetadata === null || clickstreamMetadata === void 0 ? void 0 : clickstreamMetadata['trigger_object_nm_prefix']) + "|" + triggerObject : 'no_trigger_object'
  };
  var contextualBlockName = clickstreamMetadata === null || clickstreamMetadata === void 0 ? void 0 : (_clickstreamMetadata$2 = clickstreamMetadata['contextual_block']) === null || _clickstreamMetadata$2 === void 0 ? void 0 : _clickstreamMetadata$2[0];
  var contextualBody = contextualBlockName ? generateContextualDataFields(contextualBlockName, surveyInstanceID, surveyName) : {};
  return {
    clickstreamFields: clickstreamFields,
    contextualBody: contextualBody
  };
};
var csatFormViewEventOptions = {};
var generateViewCsatContentEventArgs = function generateViewCsatContentEventArgs(survey, questionStep) {
  var _generateClickstreamF = generateClickstreamFields(survey, questionStep),
      clickstreamFields = _generateClickstreamF.clickstreamFields,
      contextualBody = _generateClickstreamF.contextualBody;

  var eventData = _objectSpread2({
    clickstream_trigger: _objectSpread2({}, clickstreamFields, {
      trigger_type_nm: TriggerType.IMPRESSION
    })
  }, contextualBody);

  return [_objectSpread2({}, csatFormViewEventProperties, {}, eventData), csatFormViewEventOptions];
};
var generateErrorStatesViewCsatContentEventArgs = function generateErrorStatesViewCsatContentEventArgs(survey) {
  var _generateClickstreamC = generateClickstreamCompletedStatesFields(survey),
      clickstreamFields = _generateClickstreamC.clickstreamFields,
      contextualBody = _generateClickstreamC.contextualBody;

  var eventData = _objectSpread2({
    clickstream_trigger: _objectSpread2({}, clickstreamFields, {
      trigger_type_nm: TriggerType.IMPRESSION
    })
  }, contextualBody);

  return [_objectSpread2({}, csatFormViewEventProperties, {}, eventData), csatFormViewEventOptions];
};
var csatSubmitAnswerEventProperties = {
  envelope: {
    event_client_start_dtm: new Date().toISOString(),
    event_template_id: '341',
    event_template_version_id: '1',
    event_type_id: '5584',
    event_type_version_id: '2'
  },
  semantic: {
    semantic_event_nm: 'submit_answer',
    topic_tag_txt: []
  }
};
var csatSubmitAnswerEventOptions = {};
var generateCsatSubmitAnswerEventArgs = function generateCsatSubmitAnswerEventArgs(survey, questionStep, triggerObject) {
  var _generateClickstreamF2 = generateClickstreamFields(survey, questionStep, triggerObject),
      clickstreamFields = _generateClickstreamF2.clickstreamFields,
      contextualBody = _generateClickstreamF2.contextualBody;

  var clickstreamTrigger = _objectSpread2({
    clickstream_trigger: _objectSpread2({}, clickstreamFields, {
      trigger_type_nm: TriggerType.INTERACTION
    })
  }, contextualBody);

  return [_objectSpread2({}, csatSubmitAnswerEventProperties, {}, clickstreamTrigger), csatSubmitAnswerEventOptions];
};
var csatClickCompleteEventProperties = {
  envelope: {
    event_client_start_dtm: new Date().toISOString(),
    event_template_id: '341',
    event_template_version_id: '1',
    event_type_id: '5585',
    event_type_version_id: '1'
  },
  semantic: {
    semantic_event_nm: 'click_complete',
    topic_tag_txt: []
  }
};
var csatClickCompleteEventOptions = {};
var generateCsatClickCompleteEventArgs = function generateCsatClickCompleteEventArgs(survey, questionStep, triggerObject) {
  var _generateClickstreamF3 = generateClickstreamFields(survey, questionStep, triggerObject),
      clickstreamFields = _generateClickstreamF3.clickstreamFields,
      contextualBody = _generateClickstreamF3.contextualBody;

  var eventData = _objectSpread2({
    clickstream_trigger: _objectSpread2({}, clickstreamFields, {
      trigger_type_nm: TriggerType.INTERACTION
    })
  }, contextualBody);

  return [_objectSpread2({}, csatClickCompleteEventProperties, {}, eventData), csatClickCompleteEventOptions];
};
var generateErrorStatesCsatClickCompleteEventArgs = function generateErrorStatesCsatClickCompleteEventArgs(survey, triggerObject) {
  var _generateClickstreamC2 = generateClickstreamCompletedStatesFields(survey, triggerObject),
      clickstreamFields = _generateClickstreamC2.clickstreamFields,
      contextualBody = _generateClickstreamC2.contextualBody;

  var eventData = _objectSpread2({
    clickstream_trigger: _objectSpread2({}, clickstreamFields, {
      trigger_type_nm: TriggerType.INTERACTION
    })
  }, contextualBody);

  return [_objectSpread2({}, csatClickCompleteEventProperties, {}, eventData), csatClickCompleteEventOptions];
};
var csatInteractAnswerFieldEventProperties = {
  envelope: {
    event_client_start_dtm: new Date().toISOString(),
    event_template_id: '341',
    event_template_version_id: '1',
    event_type_id: '5586',
    event_type_version_id: '1'
  },
  semantic: {
    semantic_event_nm: 'interact_answer_field',
    topic_tag_txt: []
  }
};
var csatInteractAnswerFieldEventOptions = {};
var generateCsatInteractAnswerFieldEventArgs = function generateCsatInteractAnswerFieldEventArgs(survey, questionStep, triggerObject) {
  var _generateClickstreamF4 = generateClickstreamFields(survey, questionStep, triggerObject),
      clickstreamFields = _generateClickstreamF4.clickstreamFields,
      contextualBody = _generateClickstreamF4.contextualBody;

  var eventData = _objectSpread2({
    clickstream_trigger: _objectSpread2({}, clickstreamFields, {
      trigger_type_nm: TriggerType.INTERACTION
    })
  }, contextualBody);

  return [_objectSpread2({}, csatInteractAnswerFieldEventProperties, {}, eventData), csatInteractAnswerFieldEventOptions];
};
var csatAbandonFormEventProperties = {
  envelope: {
    event_client_start_dtm: new Date().toISOString(),
    event_template_id: '341',
    event_template_version_id: '1',
    event_type_id: '5587',
    event_type_version_id: '1'
  },
  semantic: {
    semantic_event_nm: 'abandon_form',
    topic_tag_txt: []
  }
};
var csatAbandonFormEventOptions = {};
var generateCsatAbandonFormEventArgs = function generateCsatAbandonFormEventArgs(survey, questionStep, triggerObject) {
  var _generateClickstreamF5 = generateClickstreamFields(survey, questionStep, triggerObject),
      clickstreamFields = _generateClickstreamF5.clickstreamFields,
      contextualBody = _generateClickstreamF5.contextualBody;

  var eventData = _objectSpread2({
    clickstream_trigger: _objectSpread2({}, clickstreamFields, {
      trigger_type_nm: TriggerType.INTERACTION
    })
  }, contextualBody);

  return [_objectSpread2({}, csatAbandonFormEventProperties, {}, eventData), csatAbandonFormEventOptions];
};

export { AnalyticsContext, TriggerObject, csatAbandonFormEventOptions, csatAbandonFormEventProperties, csatAnalyticsTracking, csatClickCompleteEventOptions, csatClickCompleteEventProperties, csatFormViewEventOptions, csatFormViewEventProperties, csatInteractAnswerFieldEventOptions, csatInteractAnswerFieldEventProperties, csatSubmitAnswerEventOptions, csatSubmitAnswerEventProperties, errorStatesClickstreamFieldsAvailable, generateClickstreamCompletedStatesFields, generateClickstreamFields, generateCsatAbandonFormEventArgs, generateCsatClickCompleteEventArgs, generateCsatInteractAnswerFieldEventArgs, generateCsatSubmitAnswerEventArgs, generateErrorStatesCsatClickCompleteEventArgs, generateErrorStatesViewCsatContentEventArgs, generateViewCsatContentEventArgs, questionClickstreamFieldsAvailable, useTrackClickstream };
